import { useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useQuery } from 'react-query';
import { Outlet, useNavigate } from 'react-router-dom';

import { Box, Skeleton, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';

import { AppErrorFallback } from 'components/AppErrorFallback';
import HeaderBar from 'components/HeaderBar';
import NaviDrawer from 'components/NaviDrawer';
import WithAuth from 'components/WithAuth';
import { useAuth } from 'contexts/authentication';
import { AppRoute } from 'share/types/routes';
import { useConfiguration } from 'src/contexts/country-config';
import { useFeatureFlags } from 'src/contexts/feature-flags';
import { logErrorBoundaryError } from 'src/utils/log-error-boundary-error/log-error-boundary-error';
import { OutletContext, User } from 'types';
import { FeatureFlag } from 'utils/constants';

import { Props, TEST_IDS } from './types';

const drawerWidth = 240;

const Main = styled('main', {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'isFullWidth'
})<{
  isFullWidth?: boolean;
  open?: boolean;
}>(({ theme, open, isFullWidth = false }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  marginLeft: `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen
    }),
    marginLeft: 0
  }),
  height: '100%',
  maxWidth: isFullWidth ? '100%' : '990px',
  margin: '0 auto'
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end'
}));

const LayoutComponent = ({
  api,
  isFullWidth,
  handleTourActive,
  handleShowTour,
  showTour,
  setSteps
}: Props) => {
  const configuration = useConfiguration();
  const auth = useAuth();
  const navigate = useNavigate();
  const hasFeature = useFeatureFlags();
  const voucher_shop_url = configuration.voucher_shop;

  const getUserQuery = useQuery<User, Error>(
    'user',
    async () => {
      return await api.user.get();
    },
    {
      enabled: Boolean(auth.token)
    }
  );

  useEffect(() => {
    if (
      getUserQuery.data &&
      !getUserQuery.data.company.is_company_management_enabled
    )
      navigate(AppRoute.UpgradeAccount);
  }, []);

  if (getUserQuery.isError) {
    // todo: add error bountry to handle errors
    return <>{getUserQuery.error?.message}</>;
  }

  if (getUserQuery.isLoading || !getUserQuery.data) {
    return (
      <Stack direction={'row'} data-testid={TEST_IDS.skeleton}>
        <Skeleton variant="rectangular" width={drawerWidth} height={'100vh'} />
        <Stack sx={{ p: 12, flexGrow: 1 }}>
          <Skeleton variant="text" width={'100%'} height={20} />
          <Skeleton variant="text" width={'100%'} height={20} />
          <Skeleton variant="text" width={'100%'} height={20} />
          <Skeleton
            variant="rectangular"
            width={'100%'}
            height={'90%'}
            sx={{ mt: 4, borderRadius: 2 }}
          />
        </Stack>
      </Stack>
    );
  }

  const user = getUserQuery.data;

  const outlet_context: OutletContext = {
    user,
    api
  };

  let exclude = configuration!.exclude;

  const isVerified = user.company.is_company_management_enabled;

  const isMenuItemEnabled: Record<string, boolean> = {
    Index: isVerified,
    Company: isVerified,
    Employee: isVerified,
    Department: isVerified,
    Location: isVerified,
    ExpenseCode: isVerified,
    CostCenter: isVerified,
    Allowance: isVerified && user.company.is_allowance_enabled,
    Subscription: isVerified && user.company.is_subscription_enabled,
    Vouchers: isVerified && !hasFeature(FeatureFlag.GiftCard),
    HelpCenter: isVerified,
    Report: isVerified,
    UpgradeAccount: !isVerified,
    GiftCard: hasFeature(FeatureFlag.GiftCard)
  };

  exclude = [
    ...new Set([
      ...exclude,
      ...(Object.keys(isMenuItemEnabled).filter(
        (key) => !isMenuItemEnabled[key]
      ) as AppRouteId[])
    ])
  ];

  return (
    <Box
      data-testid="layout"
      sx={{ display: 'flex', background: '#F7F7F7', minHeight: '100vh' }}
    >
      <NaviDrawer
        width={drawerWidth}
        exclude={exclude}
        voucherShopUrl={voucher_shop_url}
        handleTourActive={handleTourActive}
        handleShowTour={handleShowTour}
        showTour={showTour}
        setSteps={setSteps}
        isVerified={isVerified}
      />
      <HeaderBar width={drawerWidth} user={user} />
      <Main open={true} isFullWidth={isFullWidth}>
        <DrawerHeader />
        <ErrorBoundary
          FallbackComponent={(props) => (
            <AppErrorFallback
              {...props}
              title="Unexpected error occurred"
              description="Please try again. If the error persists, contact us on"
              buttonLabel="Return to Dashboard"
            />
          )}
          onReset={() => {
            navigate(AppRoute.Index);
          }}
          onError={logErrorBoundaryError}
        >
          <Outlet context={outlet_context} />
        </ErrorBoundary>
      </Main>
    </Box>
  );
};

export const Layout = WithAuth(LayoutComponent);
